import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import Layout from './components/Layout';
import BuildUpdate from './components/BuildUpdate';
import { startTelemetry, startPage, trackPending, reportError, flush, whenQuiet } from './utils/telemetry';
import { preloadModule, deferred } from './utils/preload';
import PageErrorBoundary from './components/PageErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeContextProvider, useTheme } from './contexts/ThemeContext';
import { UnitSettingsProvider } from './contexts/UnitSettingsContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FarmStatusProvider } from './contexts/FarmStatusContext';
import GlobalStyle from './styles/GlobalStyle';
// Eager, not lazy: the share link is opened cold from WhatsApp, and a separate chunk
// would be one more round trip to us-east-1 before anything useful shows (~6 KB gz).
import SharedPhotos from './pages/SharedPhotos';
import { SHARE_TOKEN } from './utils/shareLink';

// The farm assistant (~12 KB gzipped with its markdown and turn views) is only a
// floating button until tapped, so it is its own chunk, fetched during the sign-in
// check instead of delaying it (utils/preload.js).
const ChatBubble = deferred(preloadModule(() => import(/* webpackChunkName: "chat" */ './components/ChatBubble')));

// Dashboard is where almost every session lands, so it stays in the main bundle —
// no Suspense flash on the most common first paint. Every other page is its own
// chunk: the app was one 1.16 MB bundle for all 16 pages, which is what a first
// visit downloaded just to show the Dashboard. Over the farm's link to a
// us-east-1 server that download alone was seconds before anything rendered.
const pageLoaders = {
  HydroUnits: () => import('./pages/HydroUnits'),
  HydroUnitDetail: () => import('./pages/HydroUnitDetail'),
  Rooms: () => import('./pages/Rooms'),
  Power: () => import('./pages/Power'),
  CameraMonitoring: () => import('./pages/CameraMonitoring'),
  DataExport: () => import('./pages/DataExport'),
  Settings: () => import('./pages/Settings'),
  ManualSensorEntry: () => import('./pages/ManualSensorEntry'),
  PlantLog: () => import('./pages/PlantLog'),
  PlantViewer: () => import('./pages/PlantViewer'),
  PlantDetail: () => import('./pages/PlantDetail'),
  ManagerLog: () => import('./pages/ManagerLog'),
  Nutrients: () => import('./pages/Nutrients'),
  Calendar: () => import('./pages/Calendar'),
  SprayLog: () => import('./pages/SprayLog'),
  RackPhotos: () => import('./pages/RackPhotos'),
  Issues: () => import('./pages/Issues'),
  Purchases: () => import('./pages/Purchases'),
  PhTest: () => import('./pages/PhTest'),
  Activity: () => import('./pages/Activity'),
};

// Chunk names are content hashes, so a deploy deletes the files an open tab still
// points at: its next visit to a not-yet-loaded page 404s and the import rejects.
// Reload once to pick up the new build (the guard stops a loop if the server is
// really down); any other failure goes to PageErrorBoundary.
const RELOAD_KEY = 'chunkReloadAt';

function lazyPage(load) {
  return lazy(() => trackPending(load()).catch((err) => {
    const stale = err?.name === 'ChunkLoadError' || /Loading (CSS )?chunk/i.test(err?.message || '');
    reportError('chunk_error', err?.message, { stale });
    flush();
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (stale && Date.now() - last > 30000) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return new Promise(() => {});   // keep the fallback up until the reload lands
    }
    throw err;
  }));
}

const HydroUnits = lazyPage(pageLoaders.HydroUnits);
const HydroUnitDetail = lazyPage(pageLoaders.HydroUnitDetail);
const Rooms = lazyPage(pageLoaders.Rooms);
const Power = lazyPage(pageLoaders.Power);
const CameraMonitoring = lazyPage(pageLoaders.CameraMonitoring);
const DataExport = lazyPage(pageLoaders.DataExport);
const Settings = lazyPage(pageLoaders.Settings);
const ManualSensorEntry = lazyPage(pageLoaders.ManualSensorEntry);
const PlantLog = lazyPage(pageLoaders.PlantLog);
const PlantViewer = lazyPage(pageLoaders.PlantViewer);
const PlantDetail = lazyPage(pageLoaders.PlantDetail);
const ManagerLog = lazyPage(pageLoaders.ManagerLog);
const Nutrients = lazyPage(pageLoaders.Nutrients);
const Calendar = lazyPage(pageLoaders.Calendar);
const SprayLog = lazyPage(pageLoaders.SprayLog);
const RackPhotos = lazyPage(pageLoaders.RackPhotos);
const Issues = lazyPage(pageLoaders.Issues);
const Purchases = lazyPage(pageLoaders.Purchases);
const PhTest = lazyPage(pageLoaders.PhTest);
const Activity = lazyPage(pageLoaders.Activity);

// Splitting alone made the FIRST visit to every page wait on its chunk — one more
// ~250 ms round trip to us-east-1 before the page could even start fetching its
// data. So once the app is up and idle, pull every chunk into the browser cache
// (~225 KB gzipped in total, cached for a year). import() is memoised by webpack,
// so a later navigation resolves immediately instead of hitting the network.
// It waits for the landing page's own requests to finish first: started on the
// first idle moment, those 225 KB shared the phone's slow link with the
// Dashboard's ~30 API responses, right when the manager is waiting for them.
function usePrefetchPages() {
  useEffect(() => {
    const warm = () => Object.values(pageLoaders).forEach((load) => load().catch(() => {}));
    let idleId = null;
    const cancelWait = whenQuiet(() => {
      if ('requestIdleCallback' in window) idleId = window.requestIdleCallback(warm, { timeout: 2000 });
      else warm();
    }, 10000);
    return () => {
      cancelWait();
      if (idleId !== null) window.cancelIdleCallback(idleId);
    };
  }, []);
}

// Shown while the initial /auth/me session check is in flight, so the login
// screen does not flash for users who are already signed in.
function AuthLoading() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif',
      color: '#6b7280',
      fontSize: '0.9rem',
    }}>
      Loading…
    </div>
  );
}

// In Layout's content area, not a full-screen overlay — the sidebar and header
// stay put while a lazy page's chunk downloads, so navigating doesn't blank the
// chrome around it.
function PageLoading() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '40vh',
      color: '#6b7280',
      fontSize: '0.9rem',
    }}>
      Loading…
    </div>
  );
}

// Admin-only pages (Data export, pH test, Settings, Activity). The sidebar hides their
// links from other accounts; this also turns away a typed or bookmarked URL. The server
// refuses their API calls on its own (ADMIN_ENDPOINTS in backend/server/auth.py).
function AdminOnly({ children }) {
  const { user } = useAuth();
  return user?.role === 'admin' ? children : <Navigate to="/" replace />;
}

// Times every page: from the tap (the first one: from the cold open) until its
// requests have gone quiet (utils/telemetry.js).
function PageTimer() {
  const { pathname } = useLocation();
  const first = React.useRef(true);
  useEffect(() => {
    startPage(first.current);
    first.current = false;
  }, [pathname]);
  return null;
}

// Keyed on the path: a page that crashed is cleared as soon as you navigate away.
function PageBoundary({ children }) {
  const { pathname } = useLocation();
  return <PageErrorBoundary key={pathname}>{children}</PageErrorBoundary>;
}

function AuthenticatedApp() {
  usePrefetchPages();
  useEffect(() => { startTelemetry(); }, []);

  return (
    <UnitSettingsProvider>
      <SocketProvider>
        <FarmStatusProvider>
        <Router>
          <Layout>
            <PageBoundary>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/hydro-units" element={<HydroUnits />} />
                <Route path="/hydro-units/:unitId" element={<HydroUnitDetail />} />
                <Route path="/cameras" element={<CameraMonitoring />} />
                <Route path="/export" element={<AdminOnly><DataExport /></AdminOnly>} />
                <Route path="/rooms" element={<Rooms />} />
                <Route path="/power" element={<Power />} />
                <Route path="/settings" element={<AdminOnly><Settings /></AdminOnly>} />
                <Route path="/manual-entry" element={<ManualSensorEntry />} />
                <Route path="/plant-log" element={<PlantLog />} />
                <Route path="/plant-viewer" element={<PlantViewer />} />
                <Route path="/plant/:plantId" element={<PlantDetail />} />
                <Route path="/manager-log" element={<ManagerLog />} />
                <Route path="/nutrients" element={<Nutrients />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/spray-log" element={<SprayLog />} />
                <Route path="/rack-photos" element={<RackPhotos />} />
                <Route path="/issues" element={<Issues />} />
                <Route path="/issues/:issueId" element={<Issues />} />
                <Route path="/purchases" element={<Purchases />} />
                <Route path="/ph-test" element={<AdminOnly><PhTest /></AdminOnly>} />
                <Route path="/activity" element={<AdminOnly><Activity /></AdminOnly>} />
              </Routes>
            </Suspense>
            </PageBoundary>
          </Layout>
          {/* Inside the gate (/ai/chat needs a session) and inside the Router (answers
              link plant IDs to their pages), outside Layout so it never remounts. */}
          <ChatBubble />
          <BuildUpdate />
          <PageTimer />
        </Router>
        </FarmStatusProvider>
      </SocketProvider>
      </UnitSettingsProvider>
  );
}


function MainApp() {
  const { theme } = useTheme();
  const { isAuthenticated, loading } = useAuth();

  // Public rack-photo link: decided before the auth gate (see utils/shareLink).
  if (SHARE_TOKEN) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <SharedPhotos token={SHARE_TOKEN} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {loading ? (
        <AuthLoading />
      ) : !isAuthenticated ? (
        <Login />
      ) : (
        <AuthenticatedApp />
      )}
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <MainApp />
      </AuthProvider>
    </ThemeContextProvider>
  );
}

export default App;
